import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  FileImageOutlined,
} from '@ant-design/icons';
import {
  Descriptions,
  Flex,
  Popconfirm,
  Space,
  Typography,
  message,
} from 'antd';
import { Link, createSearchParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { usePermissions } from 'business/contextProviders/usePermissions';
import { formatReportDateStringToLocalizedDateString } from 'business/shift-report/report/services/timeOperations';
import Routes from 'config/routes';
import {
  Module_Enum,
  ShiftReportCardInfoFragment,
  useDeleteShiftReportMutation,
} from 'generated/graphql';
import { createNumberFormatter } from 'technical/format/number';
import logger from 'technical/logger';
import { useMediaType } from 'technical/media/hooks';
import { formatUserName } from 'technical/string/formatter';
import Button from 'ui/button';
import CapCard from 'ui/cap-card';

import styles from './index.module.scss';

const getMessageKey = (id: string) => `delete-shift-report:${id}`;

const getCtaLink = ({
  reportId,
  operatorValidation,
  managerValidation,
  hasManagerReadPermission,
  isMobile,
}: {
  reportId: string;
  operatorValidation: boolean;
  managerValidation: boolean;
  hasManagerReadPermission: boolean;
  isMobile: boolean;
}) => {
  // No edit on mobile, only view mode
  if (isMobile) {
    return Routes.ReportView.replace(':id', reportId);
  }

  if (
    // Operator hasn't finished the report yet
    !operatorValidation ||
    // Operator hasn't validated the report
    (!managerValidation && hasManagerReadPermission)
  ) {
    return Routes.ReportEdit.replace(':id', reportId);
  }

  return Routes.ReportView.replace(':id', reportId);
};

interface Props {
  report: ShiftReportCardInfoFragment;
  refetchList: () => Promise<unknown>;
  routeRedirection?: { redirect: string; filters?: string };
}

function ReportCard({
  report,
  refetchList,
  routeRedirection = { redirect: Routes.ReportListInProgress },
}: Props) {
  const { t, language } = useTranslation();
  const numberFormatter = createNumberFormatter(language);
  const { isMobile } = useMediaType();
  const { user } = useAppContext();
  const {
    modules: {
      SHIFT_REPORT: {
        hasManagerReadPermission,
        hasManagerEditPermission,
        isConstructionSiteFullReader,
      },
    },
  } = usePermissions();
  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'Construction site not defined');

  const gqlHeaders = useGraphqlApiHeaders(Module_Enum.ShiftReport);

  const [deleteReport] = useDeleteShiftReportMutation({
    context: {
      headers: gqlHeaders,
    },
  });

  const isUserReport = report.userId === user?.id;
  const isFinished = report.operatorValidation || isConstructionSiteFullReader;
  const isValidated =
    report.operatorValidation ||
    (currentConstructionSite.managerValidationRequired &&
      report.managerValidation);
  const reportTitle = t(`pages.report.title`, {
    date: formatReportDateStringToLocalizedDateString(report.date),
    shiftName: report.shift.name,
    context: isFinished ? 'prev' : '',
  });
  const ctaLink = getCtaLink({
    isMobile,
    reportId: report.id,
    operatorValidation: report.operatorValidation,
    managerValidation: report.managerValidation,
    hasManagerReadPermission: hasManagerReadPermission,
  });

  const fileCount = report.tasks_aggregate.files?.count ?? 0;

  const removeReport = async () => {
    try {
      message.loading({
        content: t('pages.report.list.deleteMessage'),
        key: getMessageKey(report.id),
        // No auto-dismiss
        duration: 0,
      });
      const { data, errors } = await deleteReport({
        variables: {
          id: report.id,
        },
      });
      message.destroy(getMessageKey(report.id));

      if (errors || !data?.delete_shiftReport_by_pk?.id) {
        message.warning(t('pages.report.list.deleteReportFail'));
        logger.error(errors);
      } else {
        message.success(t('pages.report.list.deleteReportSuccess'));
        await refetchList();
      }
    } catch (error) {
      message.error(t('pages.report.list.deleteReportFail'));
      logger.error(error);
    }
  };

  return (
    <CapCard
      title={
        <>
          {reportTitle}
          {fileCount > 0 ? (
            <FileImageOutlined style={{ marginLeft: 8 }} />
          ) : null}
        </>
      }
      isFinished={isFinished}
      isValidated={isValidated}
      className={styles.reportCard}
    >
      {!isFinished && <EditOutlined className={styles.editIcon} />}
      {isValidated && <CheckCircleOutlined className={styles.checkIcon} />}
      <Flex vertical align="center" className={styles.reportCardBody}>
        <Flex>
          <Descriptions
            column={1}
            className="first-info-block-width"
            layout={isMobile ? 'vertical' : 'horizontal'}
            colon={!isMobile}
          >
            <Descriptions.Item
              label={
                <Typography.Text strong>
                  {t('pages.form.creator.label')}
                </Typography.Text>
              }
            >
              {report.userName}
            </Descriptions.Item>

            {report.team && (
              <Descriptions.Item
                label={
                  <Typography.Text strong>
                    {t('pages.form.team.label')}
                  </Typography.Text>
                }
              >
                {report.team.name}
              </Descriptions.Item>
            )}

            <Descriptions.Item
              label={
                <Typography.Text strong>
                  {t('pages.form.operator.label')}
                </Typography.Text>
              }
            >
              {report.operator?.name}
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <Typography.Text strong>
                  {t('pages.form.shiftLeader.label')}
                </Typography.Text>
              }
            >
              {report.shiftLeader?.name}
            </Descriptions.Item>

            {(report.shiftEngineer || report.shiftManager) && (
              <>
                {report.shiftEngineer && (
                  <Descriptions.Item
                    label={
                      <Typography.Text strong>
                        {t('pages.form.shiftEngineer.label')}
                      </Typography.Text>
                    }
                  >
                    {formatUserName(report.shiftEngineer)}
                  </Descriptions.Item>
                )}
                {report.shiftManager && (
                  <Descriptions.Item
                    label={
                      <Typography.Text strong>
                        {t('pages.form.shiftManager.label')}
                      </Typography.Text>
                    }
                  >
                    {formatUserName(report.shiftManager)}
                  </Descriptions.Item>
                )}
              </>
            )}
          </Descriptions>

          <Descriptions
            column={1}
            size="small"
            layout={isMobile ? 'vertical' : 'horizontal'}
            colon={!isMobile}
          >
            <Descriptions.Item
              label={
                <Typography.Text strong>
                  {t('pages.form.startMetricPoint.label')}
                </Typography.Text>
              }
            >
              {numberFormatter.format(report.startMetricPoint)}
            </Descriptions.Item>
            {report.endMetricPoint ? (
              <Descriptions.Item
                label={
                  <Typography.Text strong>
                    {t('pages.form.endMetricPoint.label')}
                  </Typography.Text>
                }
              >
                {numberFormatter.format(report.endMetricPoint)}
              </Descriptions.Item>
            ) : null}

            {report.lastFinishedRing !== null &&
              report.lastFinishedRing !== -1 && (
                <Descriptions.Item
                  label={
                    <Typography.Text strong>
                      {t('pages.form.lastFinishedRing.label')}
                    </Typography.Text>
                  }
                >
                  {report.lastFinishedRing}
                </Descriptions.Item>
              )}
          </Descriptions>
        </Flex>
        <Descriptions
          column={1}
          size="small"
          layout={isMobile ? 'vertical' : 'horizontal'}
          colon={!isMobile}
        >
          <Descriptions.Item
            label={
              <Typography.Text strong>
                {t('pages.form.note.label')}
              </Typography.Text>
            }
            className={styles.comment}
          >
            {report.note ? report.note : 'N/A'}
          </Descriptions.Item>
        </Descriptions>

        <Space>
          {!isFinished && (hasManagerEditPermission || isUserReport) && (
            <Popconfirm
              title={t('pages.report.list.deletePopup')}
              icon={<ExclamationCircleFilled style={{ color: 'red' }} />}
              onConfirm={removeReport}
              okText={t('pages.report.list.delete')}
              okButtonProps={{ danger: true }}
            >
              <Button icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
          <Link
            to={{
              pathname: ctaLink,
              search: createSearchParams({
                ...routeRedirection,
              }).toString(),
            }}
          >
            <Button
              type="primary"
              htmlType="submit"
              icon={isFinished ? <EyeOutlined /> : <EditOutlined />}
            >
              {t('pages.report.ctaButton', {
                context:
                  isUserReport && !isFinished && !isMobile ? 'edit' : 'read',
              })}
            </Button>
          </Link>
        </Space>
      </Flex>
    </CapCard>
  );
}

export default ReportCard;
